import { useEffect, useState } from 'react'

/**
 * Display-only countdown for auto-answered gates.
 *
 * When a background run parks on a question/approval it auto-answers after a
 * backend-supplied window; this drives the on-screen "Auto-selecting in Ns"
 * indicator. It never performs the answer — the backend fires `auto_answered`;
 * this is purely a visual timer that resets whenever the gate opens or closes.
 */

export const GATE_COUNTDOWN_SECONDS = 10

// One tick of the countdown clamped at zero.
export const decrementCountdown = (remaining: number): number =>
  remaining <= 1 ? 0 : remaining - 1

export interface GateCountdown {
  armed: boolean
  seconds: number
}

/**
 * Resolves the countdown from the store's `gate.autoAnswerSeconds`. A number arms
 * the countdown; null/undefined means the gate is parked (no auto-answer) → no
 * countdown. Falls back to the default window if armed but unspecified.
 */
export const resolveGateCountdown = (pendingSeconds: number | null | undefined): GateCountdown => ({
  armed: pendingSeconds != null,
  seconds: pendingSeconds ?? GATE_COUNTDOWN_SECONDS,
})

export const useGateCountdown = (enabled: boolean, seconds = GATE_COUNTDOWN_SECONDS): number => {
  const [remaining, setRemaining] = useState(seconds)

  useEffect(() => {
    setRemaining(seconds)
    if (!enabled) return

    const interval = setInterval(() => setRemaining(decrementCountdown), 1000)
    return () => clearInterval(interval)
  }, [enabled, seconds])

  return remaining
}
